<template>
  <div class="gsm-autocomplete">
    <span v-if="statusIcon" :key="statusIcon.kind" ref="statusIcon" class="gsm-status-icon"
      :class="{ 'gsm-status-icon-tooltip': statusIcon.hasTooltip }"
      :aria-label="statusIcon.label"
      @mousedown.prevent="focus">
      <i class="fas" :class="statusIcon.iconClass"></i>
    </span>
    <input
      ref="input"
      type="text"
      class="form-control"
      :class="{ 'border-warning': warning, 'border-danger': unregistered, 'has-status-icon': !!statusIcon }"
      :value="value"
      :placeholder="placeholder"
      autocomplete="off"
      @input="onInput($event.target.value)"
      @keydown.down.prevent="moveActive(1)"
      @keydown.up.prevent="moveActive(-1)"
      @keydown.enter.prevent="onEnter"
      @keydown.esc="onEscape"
      @blur="onBlur">
    <span v-if="loading" class="spinner-border spinner-border-sm text-secondary gsm-spinner" role="status"></span>
    <div v-if="open" class="dropdown-menu show gsm-menu">
      <button v-for="(item, i) in suggestions" :key="'hat-' + i" ref="items" type="button"
        class="dropdown-item" :class="{ active: i === activeIndex }"
        @mousedown.prevent="select(item)">
        <span class="gsm-text">{{ item.HAT_GSMNO }}</span>
        <span class="ml-1 badge" :class="[item.DEVICE_ID ? 'badge-danger' : 'badge-success']">{{ item.DEVICE_ID ? 'KULLANIMDA' : 'BOŞTA' }}</span>
        <small class="d-block" :class="[i === activeIndex ? 'text-white-50' : 'text-secondary']">ICCID: {{ item.HAT_SERINO }}</small>
      </button>
      <span v-if="!suggestions.length" class="dropdown-item-text text-muted">Hat bulunamadı</span>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import { normalizeGsm } from '@/utils/gsm'

const MIN_SEARCH_LENGTH = 4
const SEARCH_DELAY_MS = 300

// Tooltip body'ye eklendigi icin scoped CSS islemez; genislik ve hizalama sablonda veriliyor
const TOOLTIP_TEMPLATE =
  '<div class="tooltip" role="tooltip"><div class="arrow"></div>' +
  '<div class="tooltip-inner" style="max-width: 20rem; text-align: left;"></div></div>'

// Bootstrap 4.1.3 tooltip HTML'i temizlemeden basiyor; veritabanindan gelen degerler kacislanir
function escapeHtml (value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export default {
  name: 'GsmAutocomplete',
  props: {
    value: { type: [String, Number], default: null },
    placeholder: { type: String, default: '' },
    // GSM baska bir cihazda kullaniliyorsa o cihazin bilgileri: { DEVICE_ID, IMEI_NO, CUSTOMER_TITLE, VEHICLE_NAME }
    usage: { type: Object, default: null },
    // Hat bosta ya da satirdaki cihaza esliyse input'un basinda yesil tik gosterilir
    verified: { type: Boolean, default: false },
    // GSM veritabaninda kayitli degilse input'un basinda kirmizi unlem gosterilir
    unregistered: { type: Boolean, default: false },
    warning: { type: Boolean, default: false }
  },
  data () {
    return {
      suggestions: [],
      loading: false,
      open: false,
      activeIndex: -1
    }
  },
  computed: {
    usedElsewhere () {
      return !!this.usage
    },
    // Ayni anda tek ikon gosterilir; oncelik: baska cihazda > kayitsiz > uygun
    statusIcon () {
      if (this.usedElsewhere)
        return { kind: 'elsewhere', iconClass: 'fa-exclamation-circle text-warning', hasTooltip: true,
          label: 'Bu SIM kart, girdiğiniz cihazdan farklı bir cihazda kullanılıyor' }
      if (this.unregistered)
        return { kind: 'unregistered', iconClass: 'fa-exclamation-circle text-danger', hasTooltip: true,
          label: 'Bu SIM kart, sistemde kayıtlı değil' }
      if (this.verified)
        return { kind: 'verified', iconClass: 'fa-check-circle text-success', hasTooltip: false,
          label: 'SIM kart boşta ya da bu cihaza eşli' }
      return null
    },
    statusKind () {
      return this.statusIcon ? this.statusIcon.kind : null
    },
    statusTooltipHtml () {
      if (this.usedElsewhere) return this.warningTooltipHtml
      // Global "* { color }" kurali nedeniyle metin elemanina beyaz verilir
      if (this.unregistered) return '<div class="text-white">Bu SIM kart, sistemde kayıtlı değil</div>'
      return ''
    },
    warningTooltipHtml () {
      if (!this.usage) return ''

      const details = [
        // Eski cihaz kayitlarinda IMEI_NO bos olabilir
        ['Cihaz seri no', this.usage.IMEI_NO || this.usage.DEVICE_ID],
        ['Müşteri', this.usage.CUSTOMER_TITLE],
        ['Araç', this.usage.VEHICLE_NAME]
      ]
        .filter(detail => detail[1])
        .map(detail =>
          '<div class="text-white"><span class="text-white-50">' + detail[0] + ':</span> ' + escapeHtml(detail[1]) + '</div>'
        )
        .join('')

      // Projedeki global "* { color }" kurali tooltip-inner'in beyazini ezdigi icin her metin elemanina beyaz verilir
      return '<div class="font-weight-bold text-white">Bu SIM kart, girdiğiniz cihazdan farklı bir cihazda kullanılıyor</div>' +
        '<div style="margin-top: .375rem; padding-top: .375rem; border-top: 1px solid rgba(255, 255, 255, .25);">' +
        details + '</div>'
    }
  },
  watch: {
    statusKind () {
      // Eski ikonun tooltip'i DOM'dan kalkmadan kapatilir, yeni ikon eklendikten sonra baslatilir
      this.disposeStatusTooltip()
      this.$nextTick(this.initStatusTooltip)
    }
  },
  created () {
    // Render'i etkilemedikleri icin reaktif degiller
    this.searchTimer = 0
    this.requestSeq = 0
  },
  mounted () {
    this.initStatusTooltip()
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
    this.disposeStatusTooltip()
  },
  methods: {
    focus () {
      this.$refs.input.focus()
    },
    initStatusTooltip () {
      const icon = this.$refs.statusIcon
      if (!icon || !window.$ || !this.statusIcon || !this.statusIcon.hasTooltip) return

      window.$(icon).tooltip({
        html: true,
        placement: 'top',
        template: TOOLTIP_TEMPLATE,
        // Fonksiyon oldugu icin her acilista guncel bilgiyle olusturulur
        title: () => this.statusTooltipHtml
      })
    },
    disposeStatusTooltip () {
      // Ikon kaldirilirken acik tooltip ekranda asili kalmasin
      const icon = this.$refs.statusIcon
      if (icon && window.$) window.$(icon).tooltip('dispose')
    },
    onInput (text) {
      this.$emit('input', text)
      this.activeIndex = -1
      this.scheduleSearch(normalizeGsm(text))
    },
    scheduleSearch (query) {
      clearTimeout(this.searchTimer)
      if (query.length < MIN_SEARCH_LENGTH) {
        this.cancelSearch()
        this.suggestions = []
        this.open = false
        return
      }
      this.searchTimer = setTimeout(() => this.search(query), SEARCH_DELAY_MS)
    },
    cancelSearch () {
      clearTimeout(this.searchTimer)
      // Yoldaki istegin cevabi geldiginde yok sayilsin
      this.requestSeq++
      this.loading = false
    },
    search (query) {
      const seq = ++this.requestSeq
      this.loading = true
      axios.post('/api/hatlar/single-search', { SearchFilter: query })
        .then(response => {
          if (seq !== this.requestSeq) return
          this.suggestions = Array.isArray(response.data) ? response.data : []
          this.activeIndex = -1
          this.open = document.activeElement === this.$refs.input
        })
        .catch(error => {
          if (seq !== this.requestSeq) return
          console.error(error)
          this.suggestions = []
          this.open = false
        })
        .then(() => {
          if (seq === this.requestSeq) this.loading = false
        })
    },
    moveActive (step) {
      const count = this.suggestions.length
      if (!count) return

      this.open = true
      if (this.activeIndex === -1)
        this.activeIndex = step > 0 ? 0 : count - 1
      else
        this.activeIndex = (this.activeIndex + step + count) % count

      this.$nextTick(() => {
        const items = this.$refs.items
        if (items && items[this.activeIndex]) items[this.activeIndex].scrollIntoView({ block: 'nearest' })
      })
    },
    onEnter () {
      if (this.open && this.activeIndex > -1) {
        this.select(this.suggestions[this.activeIndex])
        return
      }
      this.close()
      this.emitNormalized()
      this.$emit('commit')
      this.$emit('enter')
    },
    onEscape () {
      this.cancelSearch()
      this.close()
    },
    onBlur () {
      this.cancelSearch()
      this.close()
      this.emitNormalized()
      this.$emit('commit')
    },
    select (item) {
      this.$emit('input', normalizeGsm(item.HAT_GSMNO))
      this.$emit('select', item)
      this.close()
    },
    close () {
      this.open = false
      this.activeIndex = -1
    },
    emitNormalized () {
      if (!this.value) return
      const normalized = normalizeGsm(this.value)
      if (normalized !== this.value) this.$emit('input', normalized)
    }
  }
}
</script>

<style scoped>
  .gsm-autocomplete {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  .gsm-autocomplete .form-control {
    border-radius: 0;
    padding-right: 2rem;
  }

  .gsm-autocomplete .form-control.has-status-icon {
    padding-left: 2rem;
  }

  .gsm-status-icon {
    position: absolute;
    top: 50%;
    left: .625rem;
    z-index: 2;
    margin-top: -.5rem;
    line-height: 1rem;
  }

  .gsm-status-icon-tooltip {
    cursor: help;
  }

  .gsm-spinner {
    position: absolute;
    top: 50%;
    right: .625rem;
    margin-top: -.5rem;
  }

  .gsm-menu {
    left: auto;
    right: 0;
    min-width: 100%;
    max-height: 18rem;
    overflow-y: auto;
  }

  .gsm-text {
    font-weight: 600;
  }
</style>



// WEBPACK FOOTER //
// src/components/GsmAutocomplete.vue